import { useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import {
  getAktivitaAhrefsOverview,
  getAktivitaAhrefsHistory,
  getAktivitaAhrefsKeywords,
  type AktivitaCountry,
  type AhrefsOverview,
  type AhrefsHistory,
  type AhrefsKeywords,
  type AhrefsKeywordItem,
} from '../../api/rondo';
import { ArcSelect, ArcToolbar } from '../../components/arc/primitives/arc-primitives';
import { Card } from '../../components/arc/card/card';
import { PageState } from '../../components/arc/page-state/page-state';
import { SortableDataTable, type DataColumn } from '../../components/arc/sortable-data-table/sortable-data-table';
import { TrendChart, fmtNum } from './aktivita-shared';
import './aktivita-drill.css';

interface SeoKpi {
  key: keyof NonNullable<AhrefsOverview['metrics']>;
  label: string;
  decimal?: boolean;
}
const TILES: SeoKpi[] = [
  { key: 'domain_rating', label: 'Domain Rating', decimal: true },
  { key: 'backlinks_total', label: 'Backlinks' },
  { key: 'refdomains', label: 'Ref. domény' },
  { key: 'organic_traffic_estimated', label: 'Organic traffic' },
  { key: 'organic_keywords', label: 'Organic keywords' },
];

export function AktivitaSeoPage() {
  const [country, setCountry] = useState<AktivitaCountry>('all');

  const [overview, setOverview] = useState<AhrefsOverview | null>(null);
  const [history, setHistory] = useState<AhrefsHistory | null>(null);
  const [keywords, setKeywords] = useState<AhrefsKeywords | null>(null);
  const [tilesError, setTilesError] = useState<string | null>(null);
  const [kwError, setKwError] = useState<string | null>(null);
  const [kwLoading, setKwLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setTilesError(null);
    setKwError(null);
    setKwLoading(true);
    getAktivitaAhrefsOverview({ country })
      .then((d) => !cancelled && setOverview(d))
      .catch((e: unknown) => !cancelled && setTilesError(e instanceof ApiError ? e.message : 'Načtení selhalo'));
    getAktivitaAhrefsHistory({ country, days: 90 })
      .then((d) => !cancelled && setHistory(d))
      .catch(() => {});
    getAktivitaAhrefsKeywords({ country, limit: 30 })
      .then((d) => !cancelled && setKeywords(d))
      .catch((e: unknown) => !cancelled && setKwError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setKwLoading(false));
    return () => {
      cancelled = true;
    };
  }, [country]);

  const m = overview?.meta;
  const sync = overview?.sync;
  const tilesMeta = (() => {
    if (tilesError) return null;
    if (m && m.synced_at) return `Ahrefs sync ${m.synced_at} · ${m.domain ?? ''}`;
    if (sync && sync.reason !== 'ok' && sync.message) return sync.message;
    return 'Ahrefs: žádná data (první sync ~5:00)';
  })();
  const emptyMsg = sync && sync.reason !== 'ok' && sync.message
    ? sync.message
    : 'Data dorazí po prvním Ahrefs sync (denně 05:00).';

  const hist = history?.points ?? [];
  const labels = hist.map((p) => p.date);
  const datasets = [
    {
      label: 'Domain Rating',
      data: hist.map((p) => Number(p.domain_rating ?? 0)),
      borderColor: '#92400e',
      backgroundColor: 'rgba(146,64,14,0.08)',
      yAxisID: 'y',
      fill: false,
    },
    {
      label: 'Organic traffic',
      data: hist.map((p) => Number(p.organic_traffic_estimated ?? 0)),
      borderColor: '#047857',
      backgroundColor: 'rgba(4,120,87,0.08)',
      yAxisID: 'y2',
      fill: false,
    },
  ];
  const hFirst = hist[0];
  const hLast = hist[hist.length - 1];
  const histRange = hFirst && hLast ? `${hFirst.date} – ${hLast.date} (${hist.length} dní)` : 'žádná data';
  type KeywordRow = AhrefsKeywordItem & { rank: number } & Record<string, unknown>;
  const keywordRows: KeywordRow[] = (keywords?.items ?? []).map((item, index) => ({ ...item, rank: index + 1 }));
  const keywordColumns: DataColumn<KeywordRow>[] = [
    { key: 'rank', label: '#', width: 60, numeric: true },
    { key: 'keyword', label: 'Klíčové slovo', width: 250, render: (_, row) => <><strong>{row.keyword}</strong>{row.country && <small>{row.country}</small>}</> },
    { key: 'position', label: 'Pozice', width: 110, numeric: true, render: (_, row) => Number(row.position ?? 0).toFixed(1) },
    { key: 'traffic_estimated', label: 'Traffic est.', width: 130, numeric: true, render: (_, row) => fmtNum(row.traffic_estimated) },
    { key: 'volume', label: 'Volume', width: 120, numeric: true, render: (_, row) => fmtNum(row.volume) },
    { key: 'url', label: 'URL', width: 400, render: (_, row) => <a href={row.url} target="_blank" rel="noreferrer">{row.url}</a> },
  ];

  return (
    <section className="activity-custom-page activity-seo-page rondo-arc-activity-drill p1-arc-scope">
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">AKTIVITA</div><h1>SEO</h1><p>Autorita domén, organický dosah a klíčová slova</p></div></header>
      <ArcToolbar className="rondo-arc-activity-toolbar"><ArcSelect label="Trh" value={country} onChange={(value) => setCountry(value as AktivitaCountry)} options={[{ value: 'all', label: 'Vše' }, { value: 'cz', label: 'CZ' }, { value: 'sk', label: 'SK' }, { value: 'ro', label: 'RO' }]} /></ArcToolbar>

      {tilesError ? <PageState title="Data Ahrefs se nepodařilo načíst" message={tilesError} /> : (
        <div className="rondo-arc-activity-drill-kpis rondo-arc-activity-seo-kpis">
          {TILES.map((t) => {
            const v = Number(overview?.metrics?.[t.key] ?? 0);
            return (
              <Card title={t.label} className="rondo-arc-activity-drill-kpi" key={t.key}><strong>{t.decimal ? v.toFixed(1) : fmtNum(v)}</strong></Card>
            );
          })}
        </div>
      )}

      <Card title="Autorita a organická návštěvnost" description={histRange} className="rondo-arc-activity-trend"><div>
          <TrendChart
            labels={labels}
            datasets={datasets}
            height={260}
            axes={[
              { id: 'y', position: 'left', title: 'DR', color: '#92400e' },
              { id: 'y2', position: 'right', title: 'Traffic', color: '#047857', drawOnChartArea: false },
            ]}
          />
      </div></Card>

      <div className="rondo-arc-activity-pages-heading"><h2>Nejvýkonnější organické dotazy</h2><span>{keywordRows.length} výrazů</span></div>
      {kwLoading ? <PageState title="Načítám klíčová slova" loading /> : kwError ? <PageState title="Klíčová slova se nepodařilo načíst" message={kwError} /> : <div className="rondo-arc-activity-pages-table rondo-arc-activity-seo-table"><SortableDataTable rows={keywordRows} columns={keywordColumns} rowKey={(row) => `${row.keyword}-${row.rank}`} caption="Nejvýkonnější organické dotazy" emptyMessage={sync?.reason === 'quota' ? 'Žádná klíčová slova – vyčerpaná kvóta Ahrefs API' : 'Žádná klíčová slova (čekám na první sync)'} /></div>}

      <footer className="rondo-arc-activity-footer"><span>{emptyMsg}</span><span>{tilesMeta ?? '—'}</span></footer>
    </section>
  );
}
